import { useState } from 'react'

import { Check } from 'lucide-react'

import {
    ACOMPANAMIENTO_URL,
    ANNUAL_PRICE_EUR,
    ANNUAL_SAVE_PCT,
    type BillingCycle,
    FLAME,
    FLAME_BG,
    FOCUS,
    FOCUS_ON_DARK,
    HOW_WE_WORK,
    IN_HOUSE_ADMIN_MONTHLY_COST_EUR,
    IN_HOUSE_ADVISOR_MONTHLY_COST_EUR,
    MONTHLY_PRICE_EUR,
    MUTED,
    PRICING_INCLUDES,
    PRIMARY_CTA_LABEL,
    SURFACE_DARK_UP,
    formatPriceEur,
    scrollToId,
} from './asesoriaTokens'

export function AsesoriaPricingSection() {
    const [billingCycle, setBillingCycle] = useState<BillingCycle>('annual')
    const isAnnual = billingCycle === 'annual'
    const monthlyPriceLabel = formatPriceEur(MONTHLY_PRICE_EUR)
    const annualPriceLabel = formatPriceEur(ANNUAL_PRICE_EUR)
    const priceAmount = isAnnual ? annualPriceLabel : monthlyPriceLabel
    const pricePeriod = isAnnual ? '/año' : '/mes'
    const annualSavingsLabel = `${(MONTHLY_PRICE_EUR * 12 - ANNUAL_PRICE_EUR).toLocaleString('es-ES')} €`
    const tipiMonthlyEur = Math.round(isAnnual ? ANNUAL_PRICE_EUR / 12 : MONTHLY_PRICE_EUR)
    const savingVsAdvisorEur = Math.floor((IN_HOUSE_ADVISOR_MONTHLY_COST_EUR - tipiMonthlyEur) / 100) * 100
    const extraVsAdminEur = Math.round((tipiMonthlyEur - IN_HOUSE_ADMIN_MONTHLY_COST_EUR) / 50) * 50
    const priceHint = isAnnual
        ? 'Pago anual · de golpe · app Tipi incluida'
        : 'Pago mensual · mínimo 3 meses, se renueva cada trimestre (avísanos antes de que termine si no quieres renovar) · app Tipi incluida'

    return (
        <section id='pricing' className='scroll-mt-24 bg-[#17130E] px-6 py-16 text-[#F5EEE4] md:py-24'>
            <div className='mx-auto flex max-w-[1200px] flex-col gap-12 md:gap-14'>
                <div className='flex max-w-[820px] flex-col gap-4'>
                    <div className='text-xs font-semibold uppercase tracking-[0.14em] text-[#A89F90]'>
                        Asesoría Tipi · Hostelería rentable
                    </div>
                    <h2 className='m-0 text-[34px] font-extrabold leading-[1.05] tracking-[-0.03em] md:text-[48px]'>
                        Aquí nos dejamos la piel por tu negocio.{' '}
                        <span className={FLAME}>Si tú ganas, nosotros también.</span>
                    </h2>
                    <p className='m-0 max-w-[640px] text-[17px] leading-relaxed text-[#C9BBA8] md:text-[18px]'>
                        Recomendamos el plan anual porque la hostelería no va de un mes bueno o un mes malo: gana quien
                        controla el año entero.
                    </p>
                </div>

                <div className='grid grid-cols-1 items-start gap-10 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] lg:gap-12'>
                    <div className='flex flex-col gap-5 rounded-[28px] bg-[#F5EEE4] p-6 text-[#17130E] md:p-8'>
                        <div className='relative'>
                            <div
                                role='group'
                                aria-label='Frecuencia de pago'
                                className='grid grid-cols-2 rounded-full bg-[#E4D8C4] p-1'
                            >
                                <button
                                    type='button'
                                    aria-pressed={!isAnnual}
                                    onClick={() => setBillingCycle('monthly')}
                                    className={`rounded-full px-3 py-3 text-[14px] font-bold transition-colors sm:text-[15px] ${FOCUS} ${
                                        !isAnnual
                                            ? 'bg-[#241C16] text-[#F5EEE4]'
                                            : 'text-[#3B2214]/70 hover:text-[#17130E]'
                                    }`}
                                >
                                    Pago mensual
                                </button>
                                <button
                                    type='button'
                                    aria-pressed={isAnnual}
                                    onClick={() => setBillingCycle('annual')}
                                    className={`rounded-full px-3 py-3 text-[14px] font-bold transition-colors sm:text-[15px] ${FOCUS} ${
                                        isAnnual
                                            ? 'bg-[#241C16] text-[#F5EEE4]'
                                            : 'text-[#3B2214]/70 hover:text-[#17130E]'
                                    }`}
                                >
                                    Pago anual
                                </button>
                            </div>
                            <span className='absolute -top-2.5 right-3 rounded-full bg-[#F4502A] px-2 py-0.5 text-[11px] font-bold text-white'>
                                -{ANNUAL_SAVE_PCT}%
                            </span>
                        </div>

                        <div className='flex flex-col gap-2 pt-1'>
                            <p className='m-0 flex flex-wrap items-baseline gap-x-2 gap-y-1'>
                                <span className='text-[44px] font-extrabold tabular-nums leading-none tracking-[-0.035em] md:text-[52px]'>
                                    {priceAmount}
                                </span>
                                <span className='text-[22px] font-bold tracking-tight text-[#5C5548]'>
                                    {pricePeriod} + IVA
                                </span>
                            </p>
                            {isAnnual ? (
                                <p className='m-0 text-[15px] font-semibold text-[#2F9B63]'>
                                    Ahorras {annualSavingsLabel} al año
                                </p>
                            ) : null}
                            <p className={`m-0 text-[14px] ${MUTED}`}>{priceHint}</p>
                            <p className={`m-0 text-[14px] ${MUTED}`}>
                                Hasta 2 locales. Con más, el precio final se acuerda en la llamada.
                            </p>
                        </div>

                        <div className='relative my-1 flex items-center'>
                            <span className='h-px w-full bg-[#3B2214]/15' />
                            <span className='absolute left-1/2 flex h-8 w-8 -translate-x-1/2 items-center justify-center rounded-full bg-[#F4502A] text-[18px] font-bold text-white'>
                                +
                            </span>
                        </div>

                        <div className='flex flex-col gap-1.5 pt-2'>
                            <span
                                className={`text-[40px] font-extrabold tabular-nums leading-none tracking-[-0.035em] ${FLAME} md:text-[44px]`}
                            >
                                10 %
                            </span>
                            <span className='text-[16px] font-semibold leading-snug'>
                                de la mejora de beneficio que generemos juntos
                            </span>
                            <span className={`text-[13px] ${MUTED}`}>
                                Medida contra la auditoría inicial, ya descontado lo que pagas a Tipi. Se liquida
                                trimestralmente, con los números cerrados de la gestoría. IVA no incluido.
                            </span>
                        </div>

                        <div className='rounded-[16px] bg-[#EDE4CF] px-4 py-3.5 text-[13px] leading-relaxed text-[#3B2214]'>
                            <span className='font-bold uppercase tracking-[0.06em]'>Ejemplo:</span> Si tu beneficio
                            mejora 3.000 €/mes, el variable son 300 €/mes (900 € al trimestre). Si no mejora, no pagas
                            variable.
                        </div>

                        <button
                            type='button'
                            onClick={() => scrollToId('plaza')}
                            className={`mt-1 inline-flex min-h-[52px] w-full items-center justify-center rounded-full px-7 text-[17px] font-bold text-white shadow-[0_4px_14px_rgba(209,61,26,0.4)] transition-colors ${FLAME_BG} ${FOCUS}`}
                        >
                            {PRIMARY_CTA_LABEL}
                        </button>
                    </div>

                    <div className='flex flex-col gap-6 lg:pt-2'>
                        <h3 className='m-0 text-[22px] font-extrabold tracking-tight md:text-[24px]'>
                            Cómo trabajamos contigo
                        </h3>
                        <ol className='m-0 flex list-none flex-col gap-5 p-0'>
                            {HOW_WE_WORK.map((step, index) => (
                                <li key={step.title} className='flex gap-4'>
                                    <span className={`pt-0.5 text-[15px] font-bold tabular-nums ${FLAME}`}>
                                        {String(index + 1).padStart(2, '0')}
                                    </span>
                                    <div className='flex min-w-0 flex-col gap-1'>
                                        <span className='text-[17px] font-bold leading-snug'>{step.title}</span>
                                        <span className='text-[15px] leading-relaxed text-[#A89F90]'>{step.body}</span>
                                    </div>
                                </li>
                            ))}
                        </ol>
                        <p className='m-0 text-[14px] leading-relaxed text-[#A89F90]'>
                            Las visitas presenciales (la inicial y una por trimestre) no incluyen alojamiento ni
                            transporte: van aparte.
                        </p>
                    </div>
                </div>

                <div className={`flex flex-col gap-6 rounded-[24px] ${SURFACE_DARK_UP} px-6 py-8 md:px-8`}>
                    <h3 className='m-0 text-[20px] font-extrabold tracking-tight md:text-[22px]'>
                        Tus opciones si lo hicieras en plantilla
                    </h3>
                    <dl className='m-0 flex flex-col gap-4 text-[15px] leading-snug md:text-[16px]'>
                        <div className='flex items-start justify-between gap-6'>
                            <dt className='flex flex-col gap-1'>
                                <span className='font-bold'>Un asesor de negocio y contable poco experimentado</span>
                                <span className='text-[14px] text-[#A89F90]'>
                                    Aporta valor, pero cuesta mucho más. Coste empresa, como un chef ejecutivo o un
                                    F&amp;B manager.
                                </span>
                            </dt>
                            <dd className='m-0 whitespace-nowrap font-bold tabular-nums'>
                                {formatPriceEur(IN_HOUSE_ADVISOR_MONTHLY_COST_EUR)}/mes
                            </dd>
                        </div>
                        <div className='flex items-start justify-between gap-6'>
                            <dt className='flex flex-col gap-1'>
                                <span className='font-bold'>Un administrativo 20 h a la semana</span>
                                <span className='text-[14px] text-[#A89F90]'>
                                    Sin conocimiento del sector, registrando facturas y hablando con tu gestoría. Sale
                                    más barato, pero no te aporta criterio del sector.
                                </span>
                            </dt>
                            <dd className='m-0 whitespace-nowrap font-bold tabular-nums'>
                                {formatPriceEur(IN_HOUSE_ADMIN_MONTHLY_COST_EUR)}/mes
                            </dd>
                        </div>
                        <div className='flex items-start justify-between gap-6 border-t border-[#F5EEE4]/15 pt-4'>
                            <dt className='flex flex-col gap-1'>
                                <span className='font-bold'>Asesoría Tipi</span>
                                <span className='text-[14px] text-[#A89F90]'>
                                    Contable y asesor de hostelería, con facturas y ventas preparadas cada mes.
                                </span>
                            </dt>
                            <dd className='m-0 whitespace-nowrap font-bold tabular-nums'>
                                {formatPriceEur(tipiMonthlyEur)}/mes
                            </dd>
                        </div>
                    </dl>
                    <p className={`m-0 text-[20px] font-extrabold leading-snug md:text-[24px] ${FLAME}`}>
                        Más de {formatPriceEur(savingVsAdvisorEur)} al mes menos que un asesor en plantilla, y solo unos{' '}
                        {formatPriceEur(extraVsAdminEur)} más que un administrativo sin criterio del sector.
                    </p>
                    <p className='m-0 text-[15px] text-[#C9BBA8]'>
                        Y si tu beneficio no mejora, no pagas variable: nos jugamos lo mismo que tú.
                    </p>
                </div>

                <div className={`rounded-[24px] ${SURFACE_DARK_UP} px-6 py-8 md:px-8`}>
                    <h3 className='m-0 mb-6 text-[20px] font-extrabold tracking-tight md:text-[22px]'>
                        Todo esto entra en el plan
                    </h3>
                    <ul className='m-0 grid list-none grid-cols-1 gap-x-10 gap-y-3.5 p-0 sm:grid-cols-2 lg:grid-cols-3'>
                        {PRICING_INCLUDES.map(item => (
                            <li key={item} className='flex items-start gap-2.5 text-[15px] leading-snug'>
                                <Check
                                    className='mt-0.5 h-4 w-4 flex-none text-[#F4502A]'
                                    strokeWidth={2.5}
                                    aria-hidden='true'
                                />
                                <span>{item}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                <p className='m-0 text-center text-[15px] leading-[1.55] text-[#A89F90] md:text-[16px]'>
                    Si todavía no necesitas que nos ocupemos de todo,{' '}
                    <a
                        href={ACOMPANAMIENTO_URL}
                        className={`font-bold ${FLAME} underline-offset-2 hover:underline ${FOCUS_ON_DARK}`}
                    >
                        empieza con el acompañamiento
                    </a>
                    .
                </p>
            </div>
        </section>
    )
}
